import { logger } from '@/lib/logger';
/**
 * envCheck — Fail-fast em build/boot quando variáveis críticas estão ausentes.
 *
 * Por que: `src/integrations/supabase/client.ts` é auto-gerado pelo Lovable Cloud
 * e não pode ser editado. Sem essa validação, env vars faltando produzem erros
 * opacos em runtime ("Failed to fetch", "Invalid URL") e tela em branco.
 *
 * Em dev: log de erro detalhado.
 * Em prod: renderiza tela amigável no #root antes do React montar.
 */

const REQUIRED = ['VITE_SUPABASE_URL', 'VITE_SUPABASE_PUBLISHABLE_KEY'] as const;

export function assertRequiredEnv(): void {
  const env = import.meta.env as Record<string, string | undefined>;
  const missing = REQUIRED.filter((k) => !env[k] || env[k]?.trim() === '');

  if (missing.length === 0) return;

  const isProd = import.meta.env.PROD;
  const message = `Configuração ausente: ${missing.join(', ')}`;

  // Best-effort Sentry (não bloqueia se não estiver disponível)
  try {
    const w = window as unknown as {
      Sentry?: { captureMessage?: (m: string, opts?: unknown) => void };
    };
    w.Sentry?.captureMessage?.(message, { level: 'fatal', tags: { setup_error: 'env_missing' } });
  } catch {
    /* noop */
  }

  if (!isProd) {
    // eslint-disable-next-line no-console
    logger.error(
      `❌ [envCheck] ${message}\n` +
        `→ Defina essas variáveis no ambiente (Lovable Cloud / .env) antes de iniciar o app.`,
    );
    throw new Error(message);
  }

  // Produção: substitui o #root por tela amigável antes do React tentar montar.
  const root = document.getElementById('root');
  if (root) {
    root.innerHTML = `
      <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;font-family:system-ui,-apple-system,sans-serif;padding:24px;background:#f4f4f4;color:#111;">
        <div style="max-width:480px;text-align:center;">
          <h1 style="font-size:22px;margin:0 0 12px;">Ops, algo saiu diferente</h1>
          <p style="margin:0 0 16px;line-height:1.5;">
            A plataforma está temporariamente indisponível por um problema de configuração.
            Nossa equipe já foi notificada.
          </p>
          <p style="margin:0;font-size:14px;color:#555;">
            Se persistir, escreva para
            <a href="mailto:suporte@auli.live" style="color:#000088;">suporte@auli.live</a>.
          </p>
        </div>
      </div>
    `;
  }
  throw new Error(message);
}
